"use client"

import { useState, useEffect, useCallback } from "react"
import "./ArchitectManager.css"
import architectService from "../../../services/api/architectService"

export default function ArchitectManagement() {
    // State for architects list
    const [architects, setArchitects] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [isInitialLoad, setIsInitialLoad] = useState(true)

    // State for form inputs
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        position: "",
        avatar: "/placeholder.svg?height=100&width=300",
    })

    // State for views
    const [selectedArchitect, setSelectedArchitect] = useState(null)
    const [showDetail, setShowDetail] = useState(false)
    const [showAddForm, setShowAddForm] = useState(false)
    const [isEditing, setIsEditing] = useState(false)

    // Fetch all architects with caching
    const fetchArchitects = useCallback(async () => {
        try {
            setLoading(true)
            const response = await architectService.getAllArchitects()
            if (Array.isArray(response)) {
                setArchitects(response)
                // Cache the data
                localStorage.setItem('architectsCache', JSON.stringify(response))
                localStorage.setItem('architectsCacheTime', Date.now().toString())
            }
        } catch (error) {
            console.error("Error fetching architects:", error)
            setError("Không thể tải danh sách kiến trúc sư")
        } finally {
            setLoading(false)
            setIsInitialLoad(false)
        }
    }, [])

    // Load cached data on mount
    useEffect(() => {
        const loadCachedData = () => {
            const cachedData = localStorage.getItem('architectsCache')
            const cacheTime = localStorage.getItem('architectsCacheTime')

            if (cachedData && cacheTime) {
                const timeDiff = Date.now() - parseInt(cacheTime)
                // Use cache if it's less than 5 minutes old
                if (timeDiff < 5 * 60 * 1000) {
                    setArchitects(JSON.parse(cachedData))
                    setIsInitialLoad(false)
                    return true
                }
            }
            return false
        }

        if (!loadCachedData()) {
            fetchArchitects()
        }
    }, [fetchArchitects])

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData({
            ...formData,
            [name]: value,
        })
    }

    // Handle image upload
    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader()
            reader.onload = (event) => {
                if (event.target && event.target.result) {
                    setFormData({
                        ...formData,
                        avatar: event.target.result.toString(),
                    })
                }
            }
            reader.readAsDataURL(e.target.files[0])
        }
    }

    // Reset form
    const resetForm = () => {
        setFormData({
            name: "",
            description: "",
            position: "",
            avatar: "/placeholder.svg?height=100&width=300",
        })
        setIsEditing(false)
        setShowAddForm(false)
    }

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            setLoading(true)
            if (isEditing && selectedArchitect) {
                const updatedArchitect = await architectService.updateArchitect(selectedArchitect._id, formData)
                setSelectedArchitect(updatedArchitect)
                // Update local list
                setArchitects(prev => prev.map(arch =>
                    arch._id === updatedArchitect._id ? updatedArchitect : arch
                ))
            } else {
                const newArchitect = await architectService.createArchitect(formData)
                // Add to local list
                setArchitects(prev => [...prev, newArchitect])
            }
            resetForm()
            if (isEditing) {
                setIsEditing(false)
            }
        } catch (error) {
            console.error("Error saving architect:", error)
            setError(isEditing ? "Không thể cập nhật kiến trúc sư" : "Không thể tạo kiến trúc sư mới")
        } finally {
            setLoading(false)
        }
    }

    // Handle architect deletion
    const handleDelete = async (id) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa kiến trúc sư này không?")) {
            try {
                setLoading(true)
                await architectService.deleteArchitect(id)
                // Remove from local list
                setArchitects(prev => prev.filter(arch => arch._id !== id))
                if (selectedArchitect && selectedArchitect._id === id) {
                    setShowDetail(false)
                    setSelectedArchitect(null)
                }
            } catch (error) {
                console.error("Error deleting architect:", error)
                setError("Không thể xóa kiến trúc sư")
            } finally {
                setLoading(false)
            }
        }
    }

    // Handle view details
    const handleViewDetails = (architect) => {
        setSelectedArchitect(architect)
        setFormData({
            name: architect.name,
            description: architect.description,
            position: architect.position,
            avatar: architect.avatar || "/placeholder.svg?height=100&width=300",
        })
        setShowDetail(true)
    }

    // Handle edit
    const handleEdit = () => {
        setIsEditing(true)
    }

    // Handle cancel edit
    const handleCancelEdit = () => {
        setIsEditing(false)
        if (selectedArchitect) {
            setFormData({
                name: selectedArchitect.name,
                description: selectedArchitect.description,
                position: selectedArchitect.position,
                avatar: selectedArchitect.avatar || "/placeholder.svg?height=100&width=300",
            })
        }
    }

    // Toggle add form
    const toggleAddForm = () => {
        if (showAddForm) {
            resetForm()
        } else {
            setShowAddForm(true)
        }
    }

    return (
        <div className="am-container">
            <h1 className="am-heading">Quản Lý Kiến Trúc Sư</h1>
            {error && <div className="error-message">{error}</div>}
            {loading && isInitialLoad && <div className="loading-spinner">Đang tải...</div>}
            <div className="am-layout">
                <div className="am-list-container">
                    <div className="am-list-header">
                        <h2 className="am-subheading">Danh Sách Kiến Trúc Sư</h2>
                        <button className="am-add-button" onClick={toggleAddForm} disabled={loading}>
                            {showAddForm ? "Hủy" : "Thêm Kiến Trúc Sư Mới"}
                        </button>
                    </div>
                    <div className="am-list">
                        {Array.isArray(architects) && architects.length > 0 ? (
                            architects.map((architect) => (
                                <div key={architect._id} className="am-card">
                                    <div className="am-image">
                                        <img
                                            src={architect.avatar || "/placeholder.svg"}
                                            alt="Ảnh kiến trúc sư"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="am-details">
                                        <h3>{architect.name}</h3>
                                        <p><strong>Chuyên môn:</strong> {architect.position}</p>
                                        <div className="am-actions">
                                            <button className="am-view-button" onClick={() => handleViewDetails(architect)}>
                                                Xem Chi Tiết
                                            </button>
                                            <button className="am-delete-button" onClick={() => handleDelete(architect._id)}>
                                                Xóa
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-architects">Không tìm thấy kiến trúc sư nào</div>
                        )}
                    </div>
                </div>

                {showAddForm && (
                    <div className="am-add-section">
                        <div className="am-add-card">
                            <h2 className="am-subheading">{isEditing ? "Cập Nhật Kiến Trúc Sư" : "Thêm Kiến Trúc Sư Mới"}</h2>
                            <form onSubmit={handleSubmit} className="am-form">
                                <div className="am-form-group">
                                    <label htmlFor="name">Tên Kiến Trúc Sư</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="am-form-group">
                                    <label htmlFor="position">Chuyên môn</label>
                                    <select
                                        id="position"
                                        name="position"
                                        value={formData.position}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select a position</option>
                                        <option value="Senior Architect">Senior Architect</option>
                                        <option value="Interior Designer">Interior Designer</option>
                                        <option value="Project Manager">Project Manager</option>
                                        <option value="Junior Architect">Junior Architect</option>
                                        <option value="Design Consultant">Design Consultant</option>
                                    </select>
                                </div>
                                <div className="am-form-group">
                                    <label htmlFor="description">Description</label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows="4"
                                        required
                                    />
                                </div>
                                <div className="am-form-group">
                                    <label htmlFor="avatar">Ảnh Đại Diện</label>
                                    <input
                                        type="file"
                                        id="avatar"
                                        name="avatar"
                                        onChange={handleImageChange}
                                        accept="image/*"
                                    />
                                    <div className="am-image-preview">
                                        <img src={formData.avatar || "/placeholder.svg"} alt="Xem trước ảnh đại diện" />
                                        <p>Ảnh này sẽ hiển thị trong danh sách kiến trúc sư</p>
                                    </div>
                                </div>
                                <div className="am-form-actions">
                                    <button type="button" className="am-cancel-button" onClick={resetForm}>
                                        Hủy
                                    </button>
                                    <button type="submit" className="am-submit-button" disabled={loading}>
                                        {loading ? "Đang xử lý..." : isEditing ? "Cập Nhật" : "Thêm Kiến Trúc Sư"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {showDetail && selectedArchitect && (
                    <div className="am-detail-section">
                        <div className="am-detail-card">
                            <h2 className="am-subheading">Chi Tiết Kiến Trúc Sư</h2>
                            <div className="am-detail-image">
                                <img src={formData.avatar || "/placeholder.svg"} alt="Ảnh chi tiết kiến trúc sư" />
                            </div>
                            {isEditing ? (
                                <form onSubmit={handleSubmit} className="am-form">
                                    <div className="am-form-group">
                                        <label htmlFor="edit-name">Tên Kiến Trúc Sư</label>
                                        <input
                                            type="text"
                                            id="edit-name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="am-form-group">
                                        <label htmlFor="edit-position">Chuyên môn</label>
                                        <select
                                            id="edit-position"
                                            name="position"
                                            value={formData.position}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="">Select a position</option>
                                            <option value="Senior Architect">Senior Architect</option>
                                            <option value="Interior Designer">Interior Designer</option>
                                            <option value="Project Manager">Project Manager</option>
                                            <option value="Junior Architect">Junior Architect</option>
                                            <option value="Design Consultant">Design Consultant</option>
                                        </select>
                                    </div>
                                    <div className="am-form-group">
                                        <label htmlFor="edit-description">Description</label>
                                        <textarea
                                            id="edit-description"
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            rows="4"
                                            required
                                        />
                                    </div>
                                    <div className="am-form-group">
                                        <label htmlFor="edit-avatar">Ảnh Đại Diện</label>
                                        <input
                                            type="file"
                                            id="edit-avatar"
                                            name="avatar"
                                            onChange={handleImageChange}
                                            accept="image/*"
                                        />
                                    </div>
                                    <div className="am-detail-actions">
                                        <button type="button" className="am-cancel-button" onClick={handleCancelEdit}>
                                            Hủy
                                        </button>
                                        <button type="submit" className="am-submit-button" disabled={loading}>
                                            {loading ? "Đang xử lý..." : "Lưu Thay Đổi"}
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <>
                                    <div className="am-detail-info">
                                        <div className="am-info-group">
                                            <label>Tên Kiến Trúc Sư</label>
                                            <div className="am-info-value">{selectedArchitect.name}</div>
                                        </div>
                                        <div className="am-info-group">
                                            <label>Chuyên môn</label>
                                            <div className="am-info-value">{selectedArchitect.position}</div>
                                        </div>
                                        <div className="am-info-group">
                                            <label>Description</label>
                                            <div className="am-info-value">{selectedArchitect.description}</div>
                                        </div>
                                    </div>
                                    <div className="am-detail-actions">
                                        <button className="am-back-button" onClick={() => setShowDetail(false)}>
                                            Quay Lại
                                        </button>
                                        <button className="am-view-button" onClick={handleEdit}>
                                            Chỉnh Sửa
                                        </button>
                                        <button className="am-delete-button" onClick={() => handleDelete(selectedArchitect._id)}>
                                            Xóa
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
} 